/**
 * Sugerencias de nombre para los hablantes a partir de la conversación (sin
 * IA, en el navegador): si alguien dice «gracias, Carla» o «Carla, ¿qué
 * opinas?» y justo después habla otra persona, probablemente esa persona es
 * Carla. Solo se sugiere; el usuario confirma.
 */
import type { TranscriptSegment } from '@eco/shared'

const NO_NOMBRES = new Set(
  (
    'Yo Hola Gracias Bueno Buenas Buenos Perfecto Oye Mira Ya Sí No Ok Okay Claro Entonces Pero Porque ' +
    'Participante Dale Listo Genial Vale Exacto Chao Adiós Señor Señora Don Doña Equipo Todos Chicos Chicas ' +
    'Lunes Martes Miércoles Jueves Viernes Sábado Domingo Enero Febrero Marzo Abril Mayo Junio Julio Agosto ' +
    'Septiembre Octubre Noviembre Diciembre Meet Zoom Teams Google Excel Slack Chile Santiago'
  ).split(' ')
)

/** Nombres propios que suenan a vocativo: al inicio o al final de la frase, junto a coma o saludo. */
export function vocatives(texto: string): string[] {
  const out = new Set<string>()
  const re =
    /(?:^|[,¡¿.!?]\s*|\b(?:[Hh]ola|[Gg]racias|[Oo]ye|[Mm]ira|[Bb]ueno|[Dd]ale|[Pp]erfecto|[Yy] tú|[Yy] vos)\s*,?\s*)([A-ZÁÉÍÓÚÑ][a-záéíóúñü]{2,})(?=\s*[,.?!]|\s*$)/g
  for (let m = re.exec(texto); m; m = re.exec(texto)) {
    const n = m[1]!
    if (!NO_NOMBRES.has(n)) out.add(n)
  }
  return [...out]
}

export interface Hint {
  speaker: string
  nombre: string
  veces: number
}

/**
 * Para cada hablante sin nombre ("Participante N"), el nombre más votado por
 * las veces que otra persona lo nombró justo antes de que hablara.
 */
export function speakerNameHints(segments: TranscriptSegment[]): Hint[] {
  const votes = new Map<string, Map<string, number>>()
  for (let i = 0; i < segments.length - 1; i++) {
    const cur = segments[i]!
    const next = segments.slice(i + 1).find((s) => s.speaker !== cur.speaker)
    if (!next || next.inicioMs - cur.finMs > 15_000) continue
    for (const nombre of vocatives(cur.texto)) {
      const m = votes.get(next.speaker) ?? new Map<string, number>()
      m.set(nombre, (m.get(nombre) ?? 0) + 1)
      votes.set(next.speaker, m)
    }
  }
  // Un nombre que el propio hablante usa para dirigirse a otros no es suyo
  const saysName = new Map<string, Set<string>>()
  for (const s of segments)
    for (const n of vocatives(s.texto))
      (saysName.get(s.speaker) ?? saysName.set(s.speaker, new Set()).get(s.speaker)!).add(n)

  const hints: Hint[] = []
  for (const [speaker, m] of votes) {
    if (!/^Participante \d+$/.test(speaker)) continue
    const best = [...m.entries()]
      .filter(([n]) => !saysName.get(speaker)?.has(n))
      .sort((a, b) => b[1] - a[1])[0]
    if (best) hints.push({ speaker, nombre: best[0], veces: best[1] })
  }
  // Si dos hablantes apuntan al mismo nombre, se queda el de más votos
  const byName = new Map<string, Hint>()
  for (const h of hints) {
    const prev = byName.get(h.nombre)
    if (!prev || h.veces > prev.veces) byName.set(h.nombre, h)
  }
  return [...byName.values()].sort((a, b) => a.speaker.localeCompare(b.speaker))
}
